import { GridHookForm } from 'vnetwork-ui'
import {
    getServiceDate,
    updateServiceTime,
    getServiceConfigoptions,
} from '@/modules/Service/services'
import { ServiceStatement } from '@/modules/Service/types'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DateField, Typogaphy, Button } from 'vnetwork-ui'
import { calculateStatementDates } from '@/modules/Contract/constants/function'
import { toDate, toDayjs } from '@/utils/function'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import UndoIcons from '@/icons/UndoIcons'
import MergeConfigoptionsModal from '@/modals/ServiceModals/MergeConfigoptionsModal'
import MergeIcons from '@/icons/MergeIcons'

interface ServiceStatementProps {
    serviceID: number
}

const ServiceStatementComponent: React.FC<ServiceStatementProps> = ({
    serviceID,
}) => {
    const { t } = useTranslation()
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false)
    const { data, renderState, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_DATES, serviceID],
        queryFn: () => getServiceDate({ serviceID }),
        enabled: serviceID !== null,
    })

    const { data: configoptions } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CONFIGOPTIONS, serviceID],
        queryFn: () => getServiceConfigoptions({ serviceID }),
        enabled: serviceID !== null,
    })

    const setValue = (data: ServiceStatement) => {
        return {
            service_time_id: data?.service_time_id,
            statement_startdate: data?.statement_startdate,
            statement_enddate: data?.statement_enddate,
            statement_range: data?.statement_range,
            first_startdate: data?.first_startdate,
            first_enddate: data?.first_enddate,
            statement:
                dayjs(data?.statement_startdate).date() === 1
                    ? 'beginning_of_month'
                    : 'day_23_to_22',
        }
    }

    const formControl = useForm<ServiceStatement>({
        defaultValues: setValue(data as ServiceStatement),
    })

    const { mutateAsync, isPending: isLoading } = useMutationWithHandler(
        {
            mutationFn: updateServiceTime,
        },
        {
            successMessage: t('update-service-time-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                refetch()
            },
        }
    )

    const handleChangeStatement = (value: string) => {
        const newCycle = calculateStatementDates(
            toDayjs(formControl.watch('first_startdate')),
            value
        )

        formControl.setValue(
            'statement_startdate',
            newCycle?.statement_startdate as Dayjs
        )
        formControl.setValue(
            'statement_enddate',
            newCycle?.statement_enddate as Dayjs
        )
    }

    const handlePreviousMonth = () => {
        const currentStart = dayjs(formControl.watch('statement_startdate'))
        const currentEnd = dayjs(formControl.watch('statement_enddate'))
        const statement = formControl.watch('statement')

        let newStart: Dayjs
        let newEnd: Dayjs

        if (statement === 'beginning_of_month') {
            // For beginning of month: move to previous month's 1st to last day
            newStart = currentStart
                .subtract(1, 'month')
                .startOf('month') as Dayjs
            newEnd = currentStart.subtract(1, 'month').endOf('month') as Dayjs
        } else {
            // For day_23_to_22: move back exactly 1 month while maintaining the cycle
            newStart = currentStart.subtract(1, 'month') as Dayjs
            newEnd = currentEnd.subtract(1, 'month') as Dayjs
        }

        formControl.setValue('statement_startdate', newStart)
        formControl.setValue('statement_enddate', newEnd)
        // sao nó k có bắt đc event cái thay isDirty nhỉ
        formControl.trigger('statement_startdate')
        formControl.trigger('statement_enddate')
    }

    useEffect(() => {
        if (!data) return
        formControl.reset(setValue(data as ServiceStatement))
        formControl.trigger('statement_startdate')
        formControl.trigger('statement_enddate')
    }, [data])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const onSubmit = async (values: ServiceStatement) => {
        mutateAsync({
            service_id: serviceID,
            data: {
                statement_startdate: toDate(values?.statement_startdate),
                statement_enddate: toDate(values?.statement_enddate),
                renew_range: values?.statement_range,
            },
        })
    }

    return (
        <div className="bg-white rounded-lg mt-2 p-4">
            <div className="flex items-center justify-between">
                <Typogaphy className="font-medium text-primary">
                    {t(`service_times.statement`)}
                </Typogaphy>
            </div>

            <GridHookForm
                className="mt-4"
                formControl={formControl}
                actions={[
                    {
                        title: t('back-to-previous-month'),
                        type: 'button',
                        variant: 'outlined',
                        handler: handlePreviousMonth,
                        startIcon: <UndoIcons />,
                    },
                    {
                        title: t('edit'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isLoading,
                        disabled: isLoading || !formControl?.formState?.isDirty,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        className: 'col-span-2',
                        formItemProps: {
                            name: `statement`,
                            type: 'select',
                            placeholder: t('service-time-statement'),
                            label: t('service-time-statement'),
                            allowClear: false,
                            options: [
                                {
                                    label: t('beginning_of_month'),
                                    value: 'beginning_of_month',
                                },
                                {
                                    label: t('day_23_to_22'),
                                    value: 'day_23_to_22',
                                },
                            ],
                            onChangeAfterHookForm: handleChangeStatement,
                        },
                    },
                    {
                        formItemProps: {
                            name: 'statement_startdate',
                            type: 'datepicker',
                            label: t('statement_startdate'),
                            placeholder: t('statement_startdate'),
                        },
                        className: 'col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'statement_enddate',
                            type: 'datepicker',
                            label: t('statement_enddate'),
                            placeholder: t('statement_enddate'),
                        },
                        className: 'col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'statement_range',
                            type: 'number',
                            label: t('statement_range_placeholder'),
                            placeholder: t('statement_range_placeholder'),
                        },
                        className: 'col-span-1',
                    },

                    {
                        className: 'col-span-1',
                        renderEditComponent() {
                            return (
                                <div className="relative">
                                    <DateField
                                        disabled
                                        value={dayjs(
                                            formControl.watch(
                                                'statement_enddate'
                                            )
                                        ).add(
                                            formControl.watch(
                                                'statement_range'
                                            ) as number,
                                            'days'
                                        )}
                                    />
                                </div>
                            )
                        },
                    },
                ]}
            />
            <Button
                variant="outlined"
                startIcon={<MergeIcons />}
                onClick={() => setIsMergeModalOpen(true)}
                disabled={!configoptions || configoptions.length <= 1}
            >
                {t('merge-configoptions')}
            </Button>

            <MergeConfigoptionsModal
                open={isMergeModalOpen}
                onClose={() => setIsMergeModalOpen(false)}
                serviceID={serviceID}
            />
        </div>
    )
}

export default ServiceStatementComponent
